import { DependencyList, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDebounceEffect } from 'ahooks';
import { useRouteMatch } from 'umi';
import routes from '@/../routes/routes';
import { uploadPageLogData, IStatisticsInfo } from './utils';
import { isObject } from '@/utils';
import logStorage from './logStorage';

interface IPageLoadParams {
  pageInfo?: Record<string, string | number | undefined>;
  cabinetInfo?: Record<string, Record<string, string | number | undefined>>;
  // 是否上报页面曝光日志
  logPageLoad?: boolean;
  // 组件卸载时是否保留页面数据
  keepPageInfo?: boolean;
  // 延迟上报数据
  delay?: number;
}

type IUseLogCallbackInfo = IStatisticsInfo & {
  // 动态获取回调函数的参数，用于
  intercept?: (...arg: any[]) => Record<string, any>;
};

/**
 * 获取routes路由配置上的页面日志名称
 */
export function usePageName() {
  // 记录当前页面的路由数据
  try {
    const route = useRouteMatch();
    const routeConfig = routes.find(item => item.path === route?.path) as {
      meta?: { analytic?: string };
    };
    return routeConfig?.meta?.analytic || '';
  } catch {
    return '';
  }
}

/**
 * 事件装饰器，当函数调用时记录：主要用于点击等事件的触发日志记录
 */
export function useLogCallback<T extends (...args: any[]) => any>(
  statisticsInfo: IUseLogCallbackInfo | string,
  fn: T,
  deps: DependencyList,
) {
  const pageName = usePageName();
  // 日志数据不参与依赖
  const statisticsRef = useRef<{
    page: string;
    pageLogType?: string;
    data: IUseLogCallbackInfo;
  } | null>(null);

  try {
    statisticsRef.current = {
      page: pageName,
      pageLogType: isObject(statisticsInfo)
        ? statisticsInfo.pageLogType
        : statisticsInfo,
      data: isObject(statisticsInfo) ? statisticsInfo : {},
    };
  } catch {
    // do nothing
  }

  const cb = useCallback(fn, deps);
  return useCallback(
    (...args) => {
      const { page, pageLogType, data } = statisticsRef.current || {};

      if (data?.intercept) {
        try {
          let interceptData = data.intercept(...args);
          interceptData = { ...data, ...interceptData };
          delete interceptData.intercept;

          uploadPageLogData(page, pageLogType, interceptData);
        } catch {
          uploadPageLogData(page, pageLogType, data);
        }
      } else {
        uploadPageLogData(page, pageLogType, data);
      }

      cb(...args);

      // 回调函数执行完成后清除moduleMark
      // 首页点击埋点之后调整到新webview后，不再需要这个moduleMark 因此需要清除moduleMark
      if (data?.clearModuleMarkAfterCallback) {
        logStorage.setModuleMark(undefined);
      }
    },
    [cb],
  );
}

/**
 * 页面加载曝光日志记录
 * 设置页面日志数据与页面事件专用数据
 * 页面卸载时清楚页面数据与页面事件专用数据
 */
export function useLogPageLoad(
  fn: () => IPageLoadParams | undefined,
  deps: DependencyList | undefined,
) {
  const ref = useRef<Function>(fn);
  ref.current = fn;

  const pageName = usePageName();
  logStorage.setPageInfo({ pageName });

  // 页面卸载时清除页面数据
  useEffect(() => {
    return () => {
      console.log(`清除页面埋点数据`);
      logStorage.resetPageInfo();
    };
  }, []);

  return useDebounceEffect(() => {
    const data = ref.current?.();
    if (data) {
      logStorage.setPageInfo(data.pageInfo);
      logStorage.setPageCabinet(data.cabinetInfo);
      if (data.logPageLoad) {
        if (data.delay) {
          setTimeout(() => {
            uploadPageLogData(pageName, 'load');
          }, Number(data.delay) || 1000);
        } else {
          uploadPageLogData(pageName, 'load');
        }
      }
    }
  }, deps);
}

/**
 * 设置页面日志数据
 * 页面卸载时清楚页面数据与页面事件专用数据
 */
export function useLogPageInfoUpdate(
  fn: () => Record<string, string | number | undefined> | undefined,
  deps: DependencyList | undefined,
) {
  const ref = useRef<Function>(fn);
  ref.current = fn;

  // 页面退出时要移除数据
  useEffect(() => {
    const pageInfo = ref.current?.();
    if (!pageInfo) {
      return () => {
        // do nothing
      };
    }
    logStorage.setPageInfo(pageInfo);

    return () => {
      logStorage.resetPageInfo();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * 事件级日志数据更新
 */
export function useLogCabinetInfoUpdate(
  key?: string | Record<string, Record<string, string | number | undefined>>,
  value?: Record<string, string | number | undefined>,
) {
  useMemo(() => {
    if (!key) {
      return;
    }
    logStorage.setPageCabinet(key, value);
  }, [key, value]);
}
